import { Injectable } from "@angular/core";
import {
  CanActivate,
  CanActivateChild,
  CanLoad,
  Route,
  UrlSegment,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
  Router,
} from "@angular/router";
import { BehaviorSubject, from, Observable, of } from "rxjs";
import { LocalStorageService } from "../../services/local-storage.service";
import { HttpRequestsService } from "src/app/services/http-requests.service";
import { constant } from "src/app/common/constant";
import { catchError, map, switchMap } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class AuthGuard implements CanActivate, CanActivateChild, CanLoad {
  userRoles: any;
  constructor(
    private localStorageService: LocalStorageService,
    private router: Router,
    private httprequest: HttpRequestsService
  ) {}
  private rolesSubject = new BehaviorSubject<any>(null);
  roles$ = this.rolesSubject.asObservable();

  // Admin-driven account lockout (Active/Inactive/Blacklisted + per-capability
  // flags), piggybacked on the same check-business-activation-status call
  // this guard already makes on every route. null until the first
  // successful check; components should treat null as "not yet known,
  // don't block" rather than "blocked", since it's also null for logged-out
  // states this guard doesn't reach.
  private lockoutSubject = new BehaviorSubject<any>(null);
  lockout$ = this.lockoutSubject.asObservable();

  canActivate(
    _next: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.checkUserAuthentication(_state.url);
  }

  canActivateChild(
    _next: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return true;
  }

  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean | UrlTree> {
    const url = `/${segments.map((s) => s.path).join("/")}`;
    return this.checkUserAuthentication(url);
  }

  private checkUserAuthentication(url: string): Observable<boolean | UrlTree> {
    const isCheckoutHost = window.location.hostname === "checkout.paymish.com";
    if (isCheckoutHost && !url.startsWith("/checkout/")) {
      return of(false);
    }

    if (
      this.localStorageService.getLocalStore("LoggedUser") &&
      this.localStorageService.isLoggedIn("token")
    ) {
      return from(
        this.httprequest.get(constant.APIROUTES.CHECK_BUSINESS_STATUS, false)
      ).pipe(
        switchMap((response: any) => {
          const isBusinessActive = response?.isBusinessActivetd;
          const roles = response?.roles || {};
          this.userRoles = roles;
          this.rolesSubject.next(roles);
          this.lockoutSubject.next(response?.accountLockout || null);
          const isLive = this.localStorageService.getLocalStore("isLive");
          const businessId = this.localStorageService.getLocalStore("businessId");
          if (isBusinessActive && url === "/") {
            this.router.navigate(["dashboard"]);
            return of(false);
          }
          if (!businessId) {
            return of(true);
          }
          // Was an exact-string check against "/setting/edit/profile" -
          // the compliance wizard is one parameterized route
          // (/setting/edit/:page) covering every tab (profile, contact,
          // people, registration, address, account...), so this bounced
          // an inactive business back to Profile on EVERY navigation to
          // any other wizard step, not just Profile - including the BVN
          // verification redirect-back landing on /setting/edit/people
          // with ?message=...&businessDirectorId=..., which got silently
          // discarded entirely (dropped both the intended page and the
          // query params identifying who the redirect was for).
          if (
            !isBusinessActive &&
            !url.startsWith("/setting/edit/") &&
            isLive
          ) {
            this.router.navigate(["setting/edit/profile"]);
            return of(false);
          }

          if (!isLive) {
            return of(true);
          }

          return of(true);
        }),
        catchError((error) => {
          console.error("API call failed:", error);
          if (error?.status === 401) {
            this.router.navigate(["auth/login"]);
            return of(false);
          }
          return of(true);
        })
      );
    } else {
      if (url !== "/auth/login") {
        this.router.navigate(["auth/login"]);
      }
      return of(false);
    }
  }

  getRoles(): Observable<any> {
    return this.roles$; // Expose the observable for components to subscribe
  }

  getAccountLockout(): Observable<any> {
    return this.lockout$;
  }

  /**
   * True only when we've positively heard from the backend that this
   * capability is disabled - unknown/not-yet-loaded state (null) is
   * treated as not blocked, so the button doesn't flash disabled on
   * every page load before the check resolves.
   */
  isCapabilityBlocked(key: 'apiAccessEnabled' | 'dashboardAccessEnabled' | 'payoutsEnabled' | 'dvaEnabled' | 'checkoutEnabled'): boolean {
    const lockout = this.lockoutSubject.value;
    if (!lockout) return false;
    if (lockout.accountStatus && lockout.accountStatus !== 'active') return true;
    return lockout[key] === false;
  }

  isPermissionAllowed(key: string): boolean {
    if (!this.userRoles) return false; // Ensure roles are available
    return this.userRoles[key] === true; // Check permission based on roles
  }
}
